import { useOverlay, usePreventScroll } from '@react-aria/overlays';
import {
  type HTMLAttributes,
  type RefObject,
  useCallback,
  useState,
} from 'react';

type ControlledDrawerOptions = {
  drawerRef: RefObject<HTMLDivElement>;
  isDismissable?: boolean;
  isOpen: boolean;
  onToggle: () => void;
};

type DrawerProps = ControlledDrawerOptions | UncontrolledDrawerOptions;

type DrawerState = {
  drawerProps: HTMLAttributes<HTMLElement>;
  isOpen: boolean;
  toggle: () => void;
};

type UncontrolledDrawerOptions = {
  drawerRef: RefObject<HTMLDivElement>;
  initialOpen?: boolean;
  isDismissable?: boolean;
};

export const useDrawer = (props: DrawerProps): DrawerState => {
  const { drawerRef } = props;

  const drawerProps = {
    ...('isOpen' in props
      ? {
          drawerRef: props.drawerRef,
          isOpen: props.isOpen,
          onToggle: props.onToggle,
        }
      : {}),
    ...('initialOpen' in props
      ? {
          drawerRef: props.drawerRef,
          initialOpen: props.initialOpen,
        }
      : {}),
  };

  const [internalOpenState, setInternalIsOpenState] = useState(
    drawerProps.initialOpen ?? false,
  );

  const onToggleInternal = useCallback(() => {
    setInternalIsOpenState(!internalOpenState);
  }, [internalOpenState, setInternalIsOpenState]);

  const controlled = drawerProps.isOpen && drawerProps.onToggle;

  usePreventScroll({
    isDisabled: (controlled ? drawerProps.isOpen : internalOpenState) === false,
  });

  // this hook allows for to get the open and toggle methods to be controlled or uncontrolled
  // we always need the overlay props for both options
  const targetIsOpen = controlled ? drawerProps.isOpen : internalOpenState;
  const targetOnToggle = controlled ? drawerProps.onToggle : onToggleInternal;

  const { overlayProps } = useOverlay(
    {
      isDismissable: props.isDismissable ?? true,
      isKeyboardDismissDisabled: false,
      isOpen: targetIsOpen,
      onClose: targetOnToggle,
    },
    drawerRef,
  );

  return {
    drawerProps: overlayProps,
    isOpen: targetIsOpen ?? internalOpenState,
    toggle: targetOnToggle ?? onToggleInternal,
  };
};
